import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// componentDidCatch only works on class components; functional alternatives
// (react-error-boundary, etc.) wrap the same lifecycle hook. Kept here as a
// minimal in-house class to avoid pulling another dep — the surface is small.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production a real telemetry sink lives here; for now the console is
    // the audit trail. Avoid throwing in this handler — it would re-enter the
    // boundary and stick the app on a blank screen.
    console.error('ErrorBoundary caught:', error, info);
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.handleReset);

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
        role="alert"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 560, width: '100%', borderRadius: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportProblemOutlinedIcon color="error" />
              <Typography variant="h5" fontWeight={600}>
                Something went wrong
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The lab hit an unexpected error. Try retrying — if it happens
              again, a full reload usually clears the bad state.
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  m: 0,
                }}
              >
                {error.message}
              </Typography>
            </Paper>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={this.handleReload} color="inherit">
                Reload page
              </Button>
              <Button variant="contained" onClick={this.handleReset}>
                Try again
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }
}
